/** Stop / T1 / T2 / rr1 / rr2 for scoring v2. */

function swingLowBelow(price, lows, look = 60) {
  if (!lows?.length || price == null) return null;
  let best = null;
  for (let i = 2; i < Math.min(look, lows.length - 2); i++) {
    const l = lows[i];
    if (l >= price) continue;
    if (lows[i - 1] > l && lows[i + 1] > l) {
      if (best == null || l > best) best = l;
    }
  }
  if (best == null) {
    const slice = lows.slice(1, Math.min(look, lows.length)).filter((x) => x < price);
    if (slice.length) best = Math.max(...slice);
  }
  return best;
}

function swingHighAbove(price, highs, look = 60) {
  if (!highs?.length || price == null) return null;
  let best = null;
  for (let i = 2; i < Math.min(look, highs.length - 2); i++) {
    const h = highs[i];
    if (h <= price) continue;
    if (highs[i - 1] < h && highs[i + 1] < h) {
      if (best == null || h < best) best = h;
    }
  }
  if (best == null) {
    const slice = highs.slice(1, Math.min(look, highs.length)).filter((x) => x > price);
    if (slice.length) best = Math.min(...slice);
  }
  return best;
}

function pivotR1(high, low, close) {
  if (high == null || low == null || close == null) return null;
  const p = (high + low + close) / 3;
  return 2 * p - low;
}

function collectResistanceLevels(price, highs, lows, closes, range52w, atr) {
  const levels = [];
  if (range52w?.high != null && range52w.high > price) levels.push(range52w.high);
  const sh = swingHighAbove(price, highs);
  if (sh != null) levels.push(sh);
  if (highs?.length && lows?.length && closes?.length) {
    const r1 = pivotR1(highs[0], lows[0], closes[0]);
    if (r1 != null && r1 > price) levels.push(r1);
  }
  const swing = swingLowBelow(price, lows);
  if (swing != null && atr != null) {
    const fib = price + (price - swing) * 1.618;
    if (fib > price) levels.push(fib);
  }
  return [...new Set(levels.filter((x) => x > price))].sort((a, b) => a - b);
}

export function computeTradeLevelsV2({
  price,
  highs,
  lows,
  closes,
  sm50,
  atr,
  range52w,
}) {
  if (price == null || !Number.isFinite(price)) {
    return {
      stopPrice: null,
      t1: null,
      t2: null,
      rr1: null,
      rr2: null,
      rr1BelowTarget: false,
    };
  }
  const atrVal = atr ?? (price * 0.02);
  const swingLow = swingLowBelow(price, lows);
  let stopAnchor = swingLow;
  if (sm50 != null && price > sm50) {
    stopAnchor = stopAnchor == null ? sm50 : Math.max(stopAnchor, sm50);
  }
  if (stopAnchor == null) stopAnchor = price - 1.5 * atrVal;

  let stop = stopAnchor - 0.25 * atrVal;
  let risk = price - stop;
  const minRisk = atrVal;
  const maxRisk = 2.5 * atrVal;
  if (risk < minRisk) {
    stop = price - minRisk;
    risk = minRisk;
  } else if (risk > maxRisk) {
    stop = price - maxRisk;
    risk = maxRisk;
  }

  const resistances = collectResistanceLevels(price, highs, lows, closes, range52w, atrVal);
  const high52 = range52w?.high;
  const nearOrAboveHigh =
    high52 != null && high52 > 0 && price >= high52 * 0.97;

  let t1 = null;
  let t2 = null;
  let target_basis = "resistance";
  let rr1BelowTarget = false;

  if (nearOrAboveHigh) {
    target_basis = "projection";
    const proj1 = Math.max(2 * atrVal, 1.5 * risk);
    t1 = price + proj1;
    t2 = price + 2.5 * risk;
  } else {
    for (const lvl of resistances) {
      const reward = lvl - price;
      if (reward / risk >= 1.5) {
        t1 = lvl;
        break;
      }
    }
    if (t1 == null && high52 != null) {
      t1 = high52;
      rr1BelowTarget = (high52 - price) / risk < 1.5;
    }
    if (t1 == null) t1 = price + 1.5 * risk;

    for (const lvl of resistances) {
      if (lvl <= t1) continue;
      if ((lvl - price) / risk >= 2.5) {
        t2 = lvl;
        break;
      }
    }
    if (t2 == null) t2 = price + 2.5 * risk;
  }

  const rr1 = risk > 0 ? (t1 - price) / risk : null;
  const rr2 = risk > 0 ? (t2 - price) / risk : null;

  return {
    stopPrice: stop,
    tradeStop: stop,
    t1,
    t2,
    rr1,
    rr2,
    rr1BelowTarget,
    target_basis,
  };
}

/** Puts-side mirror: stop above swing high, rr from downside to target. */
export function computePutsRr1({ price, highs, lows, closes, sm50, atr, range52w }) {
  if (price == null) return null;
  const atrVal = atr ?? price * 0.02;
  const swingHigh = swingHighAbove(price, highs);
  let stopAnchor = swingHigh;
  if (sm50 != null && price < sm50) {
    stopAnchor = stopAnchor == null ? sm50 : Math.min(stopAnchor, sm50);
  }
  if (stopAnchor == null) stopAnchor = price + 1.5 * atrVal;
  let stop = stopAnchor + 0.25 * atrVal;
  let risk = stop - price;
  if (risk < atrVal) {
    stop = price + atrVal;
    risk = atrVal;
  } else if (risk > 2.5 * atrVal) {
    stop = price + 2.5 * atrVal;
    risk = 2.5 * atrVal;
  }
  const supports = [];
  const sl = swingLowBelow(price, lows);
  if (sl != null) supports.push(sl);
  if (range52w?.low != null && range52w.low < price) supports.push(range52w.low);
  supports.sort((a, b) => b - a);
  let target = supports[0] ?? price - 1.5 * risk;
  return risk > 0 ? (price - target) / risk : null;
}
